import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, ArrowLeft, Sparkles } from "lucide-react";
import { forwardLeadToWhatsApp } from "@/lib/whatsapp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUESTIONS = [
  {
    id: "listings",
    q: "Are your product listings optimized?",
    options: [
      { v: "yes", label: "Yes — keyword research, A+ content, pro images" },
      { v: "partial", label: "Partially — basic listings, no A+ content" },
      { v: "no", label: "No — I just uploaded the basics" },
      { v: "not_started", label: "I haven't launched yet" },
    ],
  },
  {
    id: "pricing",
    q: "Is pricing affecting your sales?",
    options: [
      { v: "competitive", label: "I'm competitive across channels" },
      { v: "high", label: "My MRP/discount mix needs work" },
      { v: "unsure", label: "Not sure — I need help benchmarking" },
      { v: "loss", label: "I'm selling at loss / negative margin" },
    ],
  },
  {
    id: "health",
    q: "Is account health impacting your visibility?",
    options: [
      { v: "great", label: "Healthy — no issues" },
      { v: "warnings", label: "Some warnings & late shipments" },
      { v: "suspended", label: "Account is suspended / restricted" },
      { v: "no_idea", label: "I don't track account health" },
    ],
  },
  {
    id: "marketplace",
    q: "Which marketplace do you most want to scale?",
    options: [
      { v: "amazon", label: "Amazon" },
      { v: "flipkart", label: "Flipkart" },
      { v: "premium", label: "Nykaa / Myntra / Tata Cliq" },
      { v: "quick", label: "Blinkit / Zepto / Instamart" },
    ],
  },
];

export default function QuizSection() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [lead, setLead] = useState({ name: "", phone: "", category: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const total = QUESTIONS.length;
  const isCapture = step === total;
  const progress = ((step + (done ? 1 : 0)) / (total + 1)) * 100;

  const choose = (qid, val) => {
    setAnswers({ ...answers, [qid]: val });
    setTimeout(() => setStep(step + 1), 200);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!lead.name || !lead.phone) {
      toast.error("Please fill name & phone");
      return;
    }
    setSubmitting(true);
    try {
      const summary = QUESTIONS.map((q) => `${q.q} → ${answers[q.id] || "n/a"}`).join("\n");
      const payload = {
        name: lead.name,
        email: `quiz-${Date.now()}@orinaenterprise.com`,
        phone: lead.phone,
        company: lead.category,
        marketplace: answers.marketplace || "",
        message: `Quiz answers:\n${summary}`,
        source: "quiz",
      };
      await axios.post(`${API}/contact`, payload);
      setDone(true);
      toast.success("Audit booked! Opening WhatsApp...");
      forwardLeadToWhatsApp(payload);
    } catch (err) {
      toast.error("Submission failed. Try WhatsApp instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid="quiz-section"
      id="quiz"
      className="border-y-2 border-black bg-[#FFD700] py-20 md:py-28"
    >
      <div className="mx-auto max-w-4xl px-4 md:px-8">
        <div className="text-center">
          <div className="tape-dark">Free · 60 seconds</div>
          <h2 className="display mt-5 text-4xl font-black leading-tight tracking-tight md:text-5xl lg:text-6xl">
            Marketplace Readiness Quiz
          </h2>
          <p className="mt-4 text-base text-black/80 md:text-lg">
            Answer 4 quick questions and get your free seller audit.
          </p>
        </div>

        {/* Progress */}
        <div className="mt-10 h-2 w-full border-2 border-black bg-white">
          <div
            data-testid="quiz-progress"
            className="h-full bg-black transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="mt-8 card-brutal bg-white p-6 md:p-10">
          {!isCapture && !done && (
            <div data-testid={`quiz-step-${step}`}>
              <div className="text-xs font-bold uppercase tracking-widest text-black/60">
                Question {step + 1} of {total}
              </div>
              <h3 className="display mt-2 text-2xl font-black leading-snug md:text-3xl">
                {QUESTIONS[step].q}
              </h3>
              <div className="mt-6 grid gap-3">
                {QUESTIONS[step].options.map((o) => (
                  <button
                    key={o.v}
                    data-testid={`quiz-opt-${step}-${o.v}`}
                    onClick={() => choose(QUESTIONS[step].id, o.v)}
                    className="flex items-center justify-between gap-4 border-2 border-black bg-white px-5 py-4 text-left text-sm font-bold transition-all hover:bg-[#FFD700] md:text-base"
                  >
                    <span>{o.label}</span>
                    <ArrowRight size={16} />
                  </button>
                ))}
              </div>
              {step > 0 && (
                <button
                  onClick={() => setStep(step - 1)}
                  className="mt-5 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-black/60 hover:text-black"
                  data-testid="quiz-back"
                >
                  <ArrowLeft size={14} /> Back
                </button>
              )}
            </div>
          )}

          {isCapture && !done && (
            <form onSubmit={submit} data-testid="quiz-capture-form">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-black/60">
                <Sparkles size={14} /> Last step
              </div>
              <h3 className="display mt-2 text-2xl font-black leading-snug md:text-3xl">
                Get your free seller audit.
              </h3>
              <p className="mt-2 text-sm text-black/70">
                Drop your details and we'll send a personalised audit + action plan within 24 hours.
              </p>
              <div className="mt-6 space-y-3">
                <input
                  data-testid="quiz-name"
                  required
                  placeholder="Full name"
                  value={lead.name}
                  onChange={(e) => setLead({ ...lead, name: e.target.value })}
                  className="w-full border-2 border-black bg-white px-4 py-3 outline-none focus:bg-[#FFFBE6]"
                />
                <input
                  data-testid="quiz-phone"
                  required
                  placeholder="Phone (+91...)"
                  value={lead.phone}
                  onChange={(e) => setLead({ ...lead, phone: e.target.value })}
                  className="w-full border-2 border-black bg-white px-4 py-3 outline-none focus:bg-[#FFFBE6]"
                />
                <input
                  data-testid="quiz-category"
                  placeholder="Business category (e.g. Beauty, Fashion, Grocery)"
                  value={lead.category}
                  onChange={(e) => setLead({ ...lead, category: e.target.value })}
                  className="w-full border-2 border-black bg-white px-4 py-3 outline-none focus:bg-[#FFFBE6]"
                />
              </div>
              <button
                disabled={submitting}
                type="submit"
                data-testid="quiz-submit"
                className="btn-brutal mt-6 w-full disabled:opacity-50"
              >
                {submitting ? "Booking..." : "Book My Free Audit"} <ArrowRight size={16} />
              </button>
              <button
                type="button"
                onClick={() => setStep(step - 1)}
                className="mt-3 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-black/60 hover:text-black"
              >
                <ArrowLeft size={14} /> Back
              </button>
            </form>
          )}

          {done && (
            <div data-testid="quiz-done" className="py-8 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center border-2 border-black bg-[#FFD700]">
                <CheckCircle2 size={28} />
              </div>
              <h3 className="display mt-5 text-3xl font-black md:text-4xl">You're booked! 🎉</h3>
              <p className="mt-3 text-base text-black/70">
                We've opened WhatsApp with your audit request. Send the message to start — or our team will call you within 24 hours.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
